// A text box that spans SEVERAL printed lines, and how type sits on them.
//
// CONSUMER: shared (the prep screen, the stamper, the detector, and
// node-pool tests).
//
// Grant, 2026-09-29, on the AAR purchase contract: "8. Additional terms
// and conditions should absolutely be one text field. But when typing
// the text it needs to start on the first line and the lines and text
// size formatting needs to perfectly match the lines." And on lines 12
// to 16 of contract page 1: "one field but a multi line field that
// automatically starts on line 12 left aligned with the proper line and
// text formatting that perfectly matches the lines."
//
// Until this module existed the OS had no idea a box could hold more
// than one line. A text field was a rectangle with a value; the stamper
// drew the value once, centred, shrunk to fit; the prep screen showed a
// label floating in the middle of the box. On a 43-line section that is
// a wall of empty rules with one sentence hanging in the centre of it.
//
// ── THE ONE CONTRACT THIS MODULE STATES ─────────────────────────────
//
// A multi-line box is described by its rectangle plus ONE number, the
// LINE PITCH: the distance in points between the printed rules it sits
// on. Everything else is derived, in exactly one place (here), so the
// detector that finds the rules, the screen that draws on them and the
// stamper that prints on them cannot disagree about where line three is:
//
//   * The box is `lineCount × pitch` tall (`lineCount` is derived back
//     as `round(height / pitch)`; a resize snaps to whole lines).
//   * Rule k (0-based) is at `y + (k + 1) × pitch − RULE_OVERHANG`, i.e.
//     each line's rule sits a couple of points above the bottom of its
//     own slot, exactly where vector-blanks.ts seats a single-line box
//     on its rule. A one-line box with pitch = height is therefore the
//     same shape the OS has always drawn — this generalises the old
//     rule rather than replacing it.
//   * Type on line k has its baseline `BASELINE_LIFT` above rule k, so
//     descenders touch the rule and nothing crosses it, which is how a
//     typewriter fills a ruled form.
//   * The first line may start further right than the rest
//     (`firstLineIndent`), because forms print a caption on the first
//     rule — "Legal Description: ______" — and then run full lines
//     under it. Text must never overprint that caption.
//
// Pure and dependency-free (the field-types.ts rule): measuring text
// is INJECTED, so the stamper measures with pdf-lib's Helvetica, the
// screen with a canvas, and a node test with a stub — and all three
// wrap identically for the same widths.

/** How far a field's box hangs below the rule it sits on. Handwriting
 *  crosses the line slightly; a box flush to it looks misaligned. The
 *  same number vector-blanks.ts has always used for a single line. */
export const MULTILINE_RULE_OVERHANG_PT = 2;

/** How far above its rule a printed line's baseline sits. Two points
 *  leaves descenders (g, y, p) touching the rule rather than crossing
 *  it, which is what typed text on a ruled form looks like. */
export const MULTILINE_BASELINE_LIFT_PT = 2;

/** Breathing room at the left of a line, so text never touches the
 *  start of the rule. Matches the stamper's single-line padding. */
export const MULTILINE_PADDING_PT = 2;

/** Pitches outside this are a mis-read, not a form. Nobody rules a
 *  writing area tighter than 6pt, and a 48pt gap is a signature block. */
export const MIN_LINE_PITCH_PT = 6;
export const MAX_LINE_PITCH_PT = 48;

/** The most of a pitch a line's type may take up. Type taller than this
 *  collides with the line above; the caption's own size is used when
 *  it fits and shrunk to this when it does not. */
const MAX_TYPE_TO_PITCH_RATIO = 0.75;

/** The shape every caller hands in — a placed field, a proposal, a
 *  layout row all satisfy it structurally. */
export interface MultilineBox {
  y: number;
  height: number;
  linePitch: number;
}

/** Whether this field is a multi-line box at all. A flag alone is not
 *  enough: a multi-line box with no usable pitch is a single-line box
 *  wearing a label, and every derivation below needs the number. */
export function isMultilineField(f: {
  fieldType?: string | undefined;
  multiline?: boolean | null | undefined;
  linePitch?: number | null | undefined;
}): f is { fieldType?: string | undefined; multiline: true; linePitch: number } {
  return (
    (f.fieldType === undefined || f.fieldType === "text") &&
    f.multiline === true &&
    typeof f.linePitch === "number" &&
    Number.isFinite(f.linePitch) &&
    f.linePitch >= MIN_LINE_PITCH_PT &&
    f.linePitch <= MAX_LINE_PITCH_PT
  );
}

// ── An OPEN box: tall, with no printed rules under it ───────────────
//
// Grant, 2026-10-27, on the Commission Disbursement Authorization: "many
// of the boxes should be multiline built they are just big boxes and the
// text just runs." A box someone drew tall over an empty area (wire
// instructions, "Additional Instructions") had no rules for the detector
// to find, so it stayed a single-line box: one line of text, centred,
// shrunk until it fit or ran off the edge.
//
// A text box tall enough for two lines of type now wraps like any other
// multi-line box. It has no rules to match, so the pitch is chosen to
// divide the box into whole lines of comfortable type.

/** The pitch an open box's lines are spaced at: the same 12pt the prep
 *  screen gives fixed text (prepare-helpers.ts FIXED_TEXT_LINE_PITCH_PT). */
export const OPEN_BOX_LINE_PITCH_PT = 12;

/** The height at which a single-line text box starts wrapping. The prep
 *  screen's default text box is 24pt tall for ONE line, so the line is
 *  drawn above that; 30pt is two and a half lines of 12pt type. */
export const OPEN_BOX_MIN_HEIGHT_PT = 30;

/**
 * The line pitch an OPEN box wraps at, or null when the box should stay a
 * single line: not a text box, already multi-line, a strike-out, or too
 * short to hold two lines. The pitch divides the box into whole lines, so
 * the last line's baseline is inside the box (never below it).
 */
export function openBoxLinePitch(f: {
  fieldType?: string | undefined;
  multiline?: boolean | null | undefined;
  linePitch?: number | null | undefined;
  height: number;
  strike?: boolean | null | undefined;
}): number | null {
  if (f.fieldType !== undefined && f.fieldType !== "text") return null;
  if (f.strike === true) return null;
  if (isMultilineField(f)) return null;
  if (!Number.isFinite(f.height) || f.height < OPEN_BOX_MIN_HEIGHT_PT) return null;
  const lines = Math.max(2, Math.floor(f.height / OPEN_BOX_LINE_PITCH_PT));
  const pitch = f.height / lines;
  return Math.min(MAX_LINE_PITCH_PT, Math.max(MIN_LINE_PITCH_PT, pitch));
}

/**
 * The same field, as a multi-line box when it is a tall open box, and
 * unchanged otherwise. Spread onto the field (never set to undefined) so a
 * single-line box gains no keys.
 */
export function withOpenBoxWrap<
  T extends {
    fieldType?: string | undefined;
    multiline?: boolean | null | undefined;
    linePitch?: number | null | undefined;
    height: number;
    strike?: boolean | null | undefined;
  },
>(f: T): T {
  const pitch = openBoxLinePitch(f);
  return pitch === null ? f : { ...f, multiline: true, linePitch: pitch };
}

/** How many printed lines a box of this height holds. Never zero: a box
 *  shorter than one pitch is still one line. */
export function multilineLineCount(height: number, linePitch: number): number {
  return Math.max(1, Math.round(height / linePitch));
}

/** The height a box needs to hold exactly this many lines. */
export function multilineHeightFor(lineCount: number, linePitch: number): number {
  return Math.max(1, Math.round(lineCount)) * linePitch;
}

/** A dragged height, snapped to the nearest whole number of lines. */
export function snapHeightToLines(height: number, linePitch: number): number {
  return multilineHeightFor(multilineLineCount(height, linePitch), linePitch);
}

/** Where each line's rule is, page-space y, top-down. */
export function multilineRuleYs(box: MultilineBox): number[] {
  const n = multilineLineCount(box.height, box.linePitch);
  const out: number[] = [];
  for (let k = 0; k < n; k++) {
    out.push(box.y + (k + 1) * box.linePitch - MULTILINE_RULE_OVERHANG_PT);
  }
  return out;
}

/** Where each line's BASELINE is, page-space y, top-down. */
export function multilineBaselineYs(box: MultilineBox): number[] {
  return multilineRuleYs(box).map((rule) => rule - MULTILINE_BASELINE_LIFT_PT);
}

/** The box a run of rules occupies: from one pitch above the first rule
 *  (plus the overhang) to just below the last. The inverse of
 *  `multilineRuleYs`, so a box built from rules reproduces those rules. */
export function multilineBoxFromRules(
  firstRuleY: number,
  lineCount: number,
  linePitch: number,
): { y: number; height: number } {
  return {
    y: firstRuleY - linePitch + MULTILINE_RULE_OVERHANG_PT,
    height: multilineHeightFor(lineCount, linePitch),
  };
}

/** The size type prints at on a multi-line box: the form's own size
 *  where it fits the pitch, and no more than three quarters of the
 *  pitch where it does not. */
export function multilineFontSize(fontSizePt: number, linePitch: number): number {
  return Math.min(fontSizePt, linePitch * MAX_TYPE_TO_PITCH_RATIO);
}

/** Measures a run of text in points at a size. Injected; see header. */
export type MeasureText = (text: string, fontSizePt: number) => number;

/**
 * Break text into the lines it prints on.
 *
 * Paragraph breaks the person typed are kept (a `\n` always starts a
 * new line, and a blank line stays blank). Within a paragraph, words
 * wrap greedily at `maxWidth`; a single word wider than the line is
 * broken by character rather than allowed to overflow, because on a
 * legal document text past the edge of its box reads as a rendering
 * fault. Runs of spaces collapse to one, the way a typewriter would
 * not but every renderer since does.
 */
export function wrapTextLines(
  text: string,
  maxWidth: number,
  fontSizePt: number,
  measure: MeasureText,
  /** The FIRST line's width, when it is narrower than the rest — an
   *  indented first line under a printed caption. Defaults to the same
   *  width as every other line. */
  firstLineWidth: number = maxWidth,
): string[] {
  const out: string[] = [];
  const width = (s: string) => measure(s, fontSizePt);
  // The line being built is the first of the box only while nothing has
  // been emitted yet; every later line, in any paragraph, is full width.
  const limit = () => (out.length === 0 ? firstLineWidth : maxWidth);
  for (const paragraph of text.replace(/\r\n?/g, "\n").split("\n")) {
    const words = paragraph.split(/[ \t]+/).filter((w) => w.length > 0);
    if (words.length === 0) {
      out.push("");
      continue;
    }
    let line = "";
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (width(candidate) <= limit()) {
        line = candidate;
        continue;
      }
      if (line) out.push(line);
      line = "";
      // The word alone may still be wider than the line.
      if (width(word) <= limit()) {
        line = word;
        continue;
      }
      let piece = "";
      for (const ch of word) {
        if (width(piece + ch) <= limit() || piece.length === 0) {
          piece += ch;
        } else {
          out.push(piece);
          piece = ch;
        }
      }
      line = piece;
    }
    out.push(line);
  }
  return out;
}

/** What a multi-line box prints, line by line, with where each line goes.
 *
 *  `overflow` is how many lines did not fit — zero means everything is
 *  on the page. A caller that prints must refuse or say so when it is
 *  not zero; silently dropping the tail of a contract clause is the
 *  accuracy rule failing on a legal document. */
export interface MultilineLayout {
  fontSize: number;
  /** The lines that fit, paired with their baseline (page y, top-down)
   *  and the x their type starts at. */
  lines: { text: string; baselineY: number; x: number }[];
  overflow: number;
  lineCount: number;
}

export function layoutMultilineText(
  box: { x: number; y: number; width: number; height: number; linePitch: number },
  text: string,
  fontSizePt: number,
  measure: MeasureText,
  firstLineIndent = 0,
): MultilineLayout {
  const fontSize = multilineFontSize(fontSizePt, box.linePitch);
  const indent = Math.max(0, Math.min(firstLineIndent, box.width - MULTILINE_PADDING_PT * 4));
  const baselines = multilineBaselineYs(box);
  const fullWidth = Math.max(1, box.width - MULTILINE_PADDING_PT * 2);
  const firstWidth = Math.max(1, fullWidth - indent);
  const lines = wrapTextLines(text, fullWidth, fontSize, measure, firstWidth);
  const placed = lines.slice(0, baselines.length).map((t, k) => ({
    text: t,
    baselineY: baselines[k]!,
    x: box.x + MULTILINE_PADDING_PT + (k === 0 ? indent : 0),
  }));
  return {
    fontSize,
    lines: placed,
    overflow: Math.max(0, lines.length - baselines.length),
    lineCount: baselines.length,
  };
}
